import React, { useState, useEffect } from "react";
import Icon from "../../../components/AppIcon";

const Sampler = ({ onTriggerSample }) => {
  const [activeCategory, setActiveCategory] = useState("all");

  const samples = [
    // --- DJ FX & DROPS ---
    { id: 0, name: "Airhorn Burst", key: "1", category: "fx", icon: "Volume2", color: "from-red-500/20 to-red-950/30 hover:from-red-500/30 border-red-800/60", text: "text-red-400", desc: "Detuned brass airhorn pulse burst" },
    { id: 1, name: "Laser Sweep", key: "2", category: "fx", icon: "Zap", color: "from-blue-500/20 to-blue-950/30 hover:from-blue-500/30 border-blue-800/60", text: "text-blue-400", desc: "Rapid exponential frequency laser drop" },
    { id: 2, name: "Sub Bass Drop", key: "3", category: "fx", icon: "ArrowDownCircle", color: "from-purple-500/20 to-purple-950/30 hover:from-purple-500/30 border-purple-800/60", text: "text-purple-400", desc: "Heavy 808 sub-bass sine drop curve" },
    { id: 3, name: "Siren Sweep", key: "4", category: "fx", icon: "AlertTriangle", color: "from-amber-500/20 to-amber-950/30 hover:from-amber-500/30 border-amber-800/60", text: "text-amber-400", desc: "Dual LFO emergency siren wave" },
    { id: 4, name: "Impact Boom", key: "5", category: "fx", icon: "Flame", color: "from-orange-500/20 to-orange-950/30 hover:from-orange-500/30 border-orange-800/60", text: "text-orange-400", desc: "Deep sub boom & noise explosion drop" },
    { id: 5, name: "Riser Uplifter", key: "6", category: "fx", icon: "TrendingUp", color: "from-emerald-500/20 to-emerald-950/30 hover:from-emerald-500/30 border-emerald-800/60", text: "text-emerald-400", desc: "Build-up pitch sweep filter uplifter" },
    { id: 6, name: "Tape Stop FX", key: "7", category: "fx", icon: "PauseCircle", color: "from-rose-500/20 to-rose-950/30 hover:from-rose-500/30 border-rose-800/60", text: "text-rose-400", desc: "Turntable brake power-off pitch dive" },
    { id: 7, name: "Vinyl Chirp", key: "8", category: "fx", icon: "Radio", color: "from-pink-500/20 to-pink-950/30 hover:from-pink-500/30 border-pink-800/60", text: "text-pink-400", desc: "FM frequency backspin scratch chirp" },

    // --- BEATS & DRUMS ---
    { id: 8, name: "Sub Kick Drum", key: "9", category: "beats", icon: "Disc", color: "from-green-500/20 to-green-950/30 hover:from-green-500/30 border-green-800/60", text: "text-green-400", desc: "Punchy 808 sub kick drum" },
    { id: 9, name: "Snare Drum", key: "0", category: "beats", icon: "Disc", color: "from-pink-500/20 to-pink-950/30 hover:from-pink-500/30 border-pink-800/60", text: "text-pink-300", desc: "High-pass noise snare with body" },
    { id: 10, name: "Hi-Hat Sizzle", key: "q", category: "beats", icon: "Radio", color: "from-cyan-500/20 to-cyan-950/30 hover:from-cyan-500/30 border-cyan-800/60", text: "text-cyan-400", desc: "Crisp high-frequency hi-hat hiss" },
    { id: 11, name: "Reverb Clap", key: "w", category: "beats", icon: "Sparkles", color: "from-purple-500/20 to-purple-950/30 hover:from-purple-500/30 border-purple-800/60", text: "text-purple-300", desc: "Multi-burst hand clap with tail" },
    { id: 12, name: "808 Cowbell", key: "e", category: "beats", icon: "Bell", color: "from-yellow-500/20 to-yellow-950/30 hover:from-yellow-500/30 border-yellow-800/60", text: "text-yellow-400", desc: "Dual-oscillator metallic TR-808 cowbell" },
    { id: 13, name: "Beat Roll Fill", key: "r", category: "beats", icon: "Activity", color: "from-indigo-500/20 to-indigo-950/30 hover:from-indigo-500/30 border-indigo-800/60", text: "text-indigo-400", desc: "4-step rhythmic drum fill pattern" },
    { id: 14, name: "Trap Rimshot", key: "t", category: "beats", icon: "Target", color: "from-teal-500/20 to-teal-950/30 hover:from-teal-500/30 border-teal-800/60", text: "text-teal-400", desc: "Sharp resonant rimshot transient" },
    { id: 15, name: "Sub Kick Roll", key: "y", category: "beats", icon: "Repeat", color: "from-lime-500/20 to-lime-950/30 hover:from-lime-500/30 border-lime-800/60", text: "text-lime-400", desc: "Triplet stutter sub-kick roll fill" },

    // --- VOCAL TAGS (formant-synthesized voice drops) ---
    { id: 16, name: "DJ Kahnu", key: "u", category: "tags", icon: "Mic", color: "from-fuchsia-500/20 to-fuchsia-950/30 hover:from-fuchsia-500/30 border-fuchsia-800/60", text: "text-fuchsia-400", desc: "\"DJ KAHNU\" voice drop — pitched down with saturation & echo trail" },
    { id: 17, name: "DJ Srikant", key: "i", category: "tags", icon: "Mic", color: "from-violet-500/20 to-violet-950/30 hover:from-violet-500/30 border-violet-800/60", text: "text-violet-400", desc: "\"DJ SRIKANT\" voice drop — pitched down with saturation & echo trail" },
  ];

  // Filtered list based on active category tab
  const filteredSamples = activeCategory === "all" 
    ? samples 
    : samples.filter(s => s.category === activeCategory);

  // Map keyboard keys to trigger samples
  useEffect(() => {
    const handleKeyDown = (e) => {
      // Don't trigger if user is typing in an input field
      if (e.target.tagName === "INPUT" || e.target.tagName === "TEXTAREA") return;
      
      const keyVal = e.key.toLowerCase();
      const sample = samples.find(s => s.key.toLowerCase() === keyVal);

      if (sample) {
        onTriggerSample(sample.id);
        
        // Trigger a temporary visual flash on the button element
        const btn = document.getElementById(`sample-pad-${sample.id}`);
        if (btn) {
          btn.classList.add("scale-95", "brightness-150", "shadow-terminal-strong", "ring-2", "ring-primary");
          setTimeout(() => {
            btn.classList.remove("scale-95", "brightness-150", "shadow-terminal-strong", "ring-2", "ring-primary");
          }, 120);
        }
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [onTriggerSample]);

  return (
    <div className="bg-card border border-border rounded-lg p-3.5 h-full flex flex-col justify-between terminal-glow">
      {/* Header & Category Switcher */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2 mb-2 border-b border-border pb-2 flex-shrink-0">
        <div className="flex items-center space-x-2">
          <div className="p-1 bg-primary/10 border border-primary/20 rounded">
            <Icon name="Activity" size={13} className="text-primary" />
          </div>
          <div>
            <h3 className="text-xs font-bold uppercase tracking-wider text-foreground">
              Synthesized DJ FX & Beats Sampler Pads
            </h3>
            <p className="text-[8px] font-source text-muted-foreground">
              {samples.filter(s => s.category !== "tags").length} Real-Time Web Audio Synthesized Samples
              {" + "}
              {samples.filter(s => s.category === "tags").length} Vocal Tags
            </p>
          </div>
        </div>

        {/* Category Tabs: ALL | DJ FX | BEATS & DRUMS */}
        <div className="flex items-center space-x-1 bg-zinc-950 border border-zinc-800 p-0.5 rounded select-none">
          <button
            type="button"
            onClick={() => setActiveCategory("all")}
            className={`text-[8px] font-bold uppercase px-2 py-0.5 rounded transition ${activeCategory === "all" ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground"}`}
          >
            All ({samples.length})
          </button>
          <button
            type="button"
            onClick={() => setActiveCategory("fx")}
            className={`text-[8px] font-bold uppercase px-2 py-0.5 rounded transition ${activeCategory === "fx" ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground"}`}
          >
            💥 DJ FX
          </button>
          <button
            type="button"
            onClick={() => setActiveCategory("beats")}
            className={`text-[8px] font-bold uppercase px-2 py-0.5 rounded transition ${activeCategory === "beats" ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground"}`}
          >
            🥁 Beats & Drums
          </button>
          <button
            type="button"
            onClick={() => setActiveCategory("tags")}
            className={`text-[8px] font-bold uppercase px-2 py-0.5 rounded transition ${activeCategory === "tags" ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground"}`}
          >
            🎙️ Voice Tags
          </button>
        </div>
      </div>

      {/* Grid of 16 MPC-Style Synthesized Pads (arranged in 4x4 layout) */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 flex-grow overflow-y-auto pr-0.5 py-1">
        {filteredSamples.map((sample) => (
          <button
            key={sample.id}
            id={`sample-pad-${sample.id}`}
            onClick={() => onTriggerSample(sample.id)}
            title={`${sample.name}: ${sample.desc}`}
            className={`flex flex-col items-center justify-between p-2 rounded-lg border bg-zinc-900/80 ${sample.color} transition duration-150 relative group active:scale-95 shadow-sm min-h-[64px]`}
          >
            {/* Key hotkey tag */}
            <span className="absolute top-1 right-1 text-[7.5px] font-fira font-bold uppercase text-zinc-300 bg-black/80 px-1 py-0.2 rounded border border-zinc-800 leading-none shadow">
              {sample.key}
            </span>

            {/* Icon */}
            <div className="my-auto mt-1">
              <Icon name={sample.icon} size={16} className={`${sample.text} group-hover:scale-110 transition duration-200`} />
            </div>

            {/* Name */}
            <div className="w-full text-center mt-1">
              <span className="text-[8.5px] font-source text-foreground font-bold tracking-tight block truncate uppercase leading-none">
                {sample.name}
              </span>
            </div>
          </button>
        ))}
      </div>

      {/* Bottom Hint Bar */}
      <div className="mt-2 text-[8.5px] font-source text-muted-foreground flex items-center justify-between bg-black/40 py-1 px-2.5 rounded border border-zinc-900 flex-shrink-0">
        <div className="flex items-center space-x-1.5">
          <Icon name="Keyboard" size={10} className="text-primary" />
          <span>Hotkeys: <strong className="text-foreground">1 to 0</strong> for FX & Beats, <strong className="text-foreground">Q W E R T Y</strong> for Drum Kit & Rolls, <strong className="text-foreground">U I</strong> for Voice Tags</span>
        </div>
        <span className="text-[8px] font-mono text-terminal-cyan">0ms Load Latency</span>
      </div>
    </div>
  );
};

export default Sampler;
